import { useNavigate, useRouterState } from "@tanstack/react-router";
import { Search, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";

/**
 * Debounced search input. Reads the current value from the URL
 * (`?<paramName>=`) and writes back through `useNavigate` so
 * filter state lives in the URL, not component state. Generic
 * across routes — uses `useRouterState` to pick up the live
 * search params without depending on a route-specific schema.
 *
 * Typing pushes a debounced `replace` navigation (300 ms); the
 * clear button pushes immediately. Both updates also strip
 * `?page=` so a search filter resets pagination back to page 1.
 */

type SearchInputProps = {
  placeholder?: string;
  paramName?: string;
};

export function SearchInput({ placeholder = "Search…", paramName = "q" }: SearchInputProps) {
  const navigate = useNavigate();
  const search = useRouterState({ select: (s) => s.location.search }) as Record<string, unknown>;
  const initial = typeof search[paramName] === "string" ? (search[paramName] as string) : "";
  const [value, setValue] = useState(initial);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const current = typeof search[paramName] === "string" ? (search[paramName] as string) : "";
    setValue(current);
  }, [search, paramName]);

  function pushUpdate(next: string) {
    navigate({
      // TanStack Router strips `undefined` search values from the URL, so
      // assigning `undefined` is equivalent to deleting the key without
      // triggering biome's noDelete rule.
      // @ts-expect-error generic component — TanStack typed routes can't
      // statically validate a search updater parameterised by a prop.
      search: (prev: Record<string, unknown>) => ({
        ...prev,
        [paramName]: next || undefined,
        page: undefined,
      }),
      replace: true,
    });
  }

  function onChange(e: React.ChangeEvent<HTMLInputElement>) {
    const next = e.target.value;
    setValue(next);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => pushUpdate(next), 300);
  }

  function onClear() {
    setValue("");
    if (timerRef.current) clearTimeout(timerRef.current);
    pushUpdate("");
  }

  return (
    <div className="relative w-full max-w-sm">
      <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
      <input
        type="text"
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className="flex h-9 w-full rounded-md border border-input bg-background pl-9 pr-9 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
      />
      {value && (
        <button
          type="button"
          onClick={onClear}
          className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-muted-foreground hover:bg-accent hover:text-accent-foreground"
          aria-label="Clear"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}
